// import Dialog from "~/components/molecules/Dialog"
import { inject, Observer } from "mobx-react"
import { WithRouterProps } from "next/dist/client/with-router"
import Head from "next/head"
import { withRouter } from "next/router"
import React from "react"

import CourseApplyDoneModal from "~/components/organisms/CourseApplyDoneModal"
import CourseInputPersonalDataModal from "~/components/organisms/CourseInputPersonalDataModal"
import CourseSchduleSelectModal from "~/components/organisms/CourseSchduleSelectModal"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import Background from "~/components/organisms/course-detail/Background"
import Consultation from "~/components/organisms/course-detail/Consultation"
import Curriculum from "~/components/organisms/course-detail/Curriculum"
import MainVisual from "~/components/organisms/course-detail/MainVisual"
import Price from "~/components/organisms/course-detail/Price"
import PromoteArea from "~/components/organisms/course-detail/PromoteArea"
import Teacher from "~/components/organisms/course-detail/Teacher"
import { Course } from "~/entities/Course"
import { TmpUserInfo } from "~/entities/User"
import { staticPath } from "~/services/helper"
import ApplicationStore from "~/stores/ApplicationStore"
import RootStore from "~/stores/RootStore"
import UiStore from "~/stores/UiStore"
import UserStore from "~/stores/UserStore"

interface Props {
  applicationStore: ApplicationStore
  course: Course
  uiStore: UiStore
  userStore: UserStore
}

interface State {
  selectedScheduleValues: string[]
  isLoading: boolean
  tmpUserInfo: TmpUserInfo
}

@inject("applicationStore", "uiStore", "userStore")
class WritingCourse extends React.Component<WithRouterProps & Props, State> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { courseStore } = initialStoreData
    const course = await courseStore.fetchById(2)
    return { course }
  }

  state = {
    selectedScheduleValues: [],
    isLoading: false,
    tmpUserInfo: {
      name: null,
      phoneNumber: null,
    },
  }

  constructor(props: WithRouterProps & Props) {
    super(props)
    const { uiStore } = props
    switch (props.router.query.openModal) {
      case "selectSchedule":
        uiStore.toggleCourseSchduleSelectModal()
        break
      case "confirmApply":
        uiStore.toggleConfirmCourseApplyModal()
        break
      case "applyComplete":
        uiStore.toggleCompleteApplyCourseModal()
        break
    }
  }

  render() {
    const { applicationStore, course, uiStore, userStore } = this.props
    const { selectedScheduleValues, isLoading, tmpUserInfo } = this.state

    const handleSelectSchedule = (schedules: string[]) => {
      this.setState({
        selectedScheduleValues: schedules,
      })
    }

    const toggleLoading = () => {
      this.setState({
        isLoading: !this.state.isLoading,
      })
    }

    const updateTmpUserInfo = (values: TmpUserInfo) => {
      this.setState({
        tmpUserInfo: values,
      })
    }

    const mainActionForSchduleSelectModal = async () => {
      toggleLoading()
      selectedScheduleValues.sort()
      if (userStore.authed) {
        await applicationStore.courseConsultationApply(2, selectedScheduleValues.sort())
      } else {
        await applicationStore.courseConsultationApplyByGuest(2, tmpUserInfo, selectedScheduleValues.sort())
      }
      uiStore.toggleCourseSchduleSelectModal()
      uiStore.toggleCompleteApplyCourseModal()
      toggleLoading()
    }

    return (
      <>
        <Head>
          <title>ライティング講座 | Infra（インフラ）</title>
          <meta
            name="description"
            content="対話で思考を紐解くインタビューの技術から、読み手の心を動かす執筆の技術まで。仕事に、自己表現に、考えの整理に応用できる「書く」スキルを学びます。"
          />
          <meta name="og:title" content="ライティング講座 | Infra（インフラ）" />
          <meta
            name="og:description"
            content="対話で思考を紐解くインタビューの技術から、読み手の心を動かす執筆の技術まで。仕事に、自己表現に、考えの整理に応用できる「書く」スキルを学びます。"
          />
          <meta name="og:image" content={process.env.APP_HOST + staticPath("/static/images/course/writing/ogp.png")} />
        </Head>
        <Header logoType="Courses" showTab />
        <MainVisual
          mSrcPc={staticPath("/static/images/course/writing/mv.png")}
          mSrcSp={staticPath("/static/images/course/writing/mv-sp.png")}
          typoPath={staticPath("/static/images/course/writing/typo.svg")}
          heading="ライティング講座"
          description="対話で思考を紐解くインタビューの技術から、読み手の心を動かす執筆の技術まで。仕事に、自己表現に、考えの整理に応用できる「書く」スキルを学びます。"
          courseLineUrl={course.lineUrl}
          disabled
        />
        <Background
          message={`ライターや編集者といえばマスコミや出版社、新聞社などが思い浮かびやすいですが、昨今ではインターネットの普及に伴い、様々な業界でメディアが立ち上がっておりライター需要は年々高まっています。
          ライターや編集者として活躍する/仕事を得るには「ポートフォリオ(実績)の有無」が大きく関わってきます。
          本講座では、みなさんに「書く力」を授け、言葉のプロとして実績をつくる舞台を提供します。
          `}
          items={[
            {
              imgNumberPc: staticPath("/static/images/course/number-1-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-1-sp.svg"),
              heading: "業界のデジタルシフト",
              description:
                "本や雑誌など、出版すれば一定の売上をあげられるという時代は終わりました。読書離れやIT化に伴い、出版社や新聞社もWeb媒体や電子書籍に力を入れており、また入稿などの業務面でもデジタル化が進んでいます。今後は紙媒体だけでなく、WEB上でも編集ができる人材が重宝され、活躍の幅も広がります。",
              linkURL: "",
              linkText: "",
            },
            {
              imgNumberPc: staticPath("/static/images/course/number-2-pc.svg"),
              imgNumberSp: staticPath("/static/images/course/number-2-sp.svg"),
              heading: "編集者・ライター不足",
              description:
                "ネット全盛期のこの時代、集客や顧客獲得のためのオンライン戦略の1つとして自社メディアを立ち上げる企業が増えています。ですが全く経験のない人にメディア運営を任せられないため、経験をもつライターや編集者の需要が年々高まっています。",
              linkURL: "",
              linkText: "",
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/writing/mv.png")}
          courseName="ライティング講座"
          courseLineUrl={course.lineUrl}
          disabled
          hideBaloon
        />
        <Teacher
          items={[
            {
              img: staticPath("/static/images/course/writing/teacher-1.png"),
              name: "オバラ ミツフミ",
              position: "株式会社モメンタム・ホース Senior Writer",
              introduction:
                "大学在学中にライターとして独立し、WEBメディアへの記事寄稿や書籍の構成を手がける。現在は株式会社モメンタム・ホースに所属しながら、SNSやブログを活用したブランディングの支援を行うなど、フリーライターとしても活動中。編集協力に『転職と副業のかけ算』（moto）『CREATE WORK』（ピョートル・フェリクス・グジバチ）など。",
            },
            {
              img: staticPath("/static/images/course/writing/teacher-2.png"),
              name: "長谷川 リョー",
              position: "株式会社モメンタム・ホース 代表取締役社長 / 編集者",
              introduction:
                "東京大学大学院学際情報学府修了後、リクルートホールディングスを経て、独立。ビジネス・テクノロジー領域を中心に数多くのベンチャー経営者や最先端で活躍する研究者やクリエイターへ取材・執筆を重ねる。編集協力に『10年後の仕事図鑑』（堀江貴文、落合陽一）、『2030年の世界地図帳』（落合陽一）、『THE TEAM』（麻野耕司）など。いずれも10万部を突破。",
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/writing/mv.png")}
          courseName="ライティング講座"
          courseLineUrl={course.lineUrl}
          disabled
          hideBaloon
        />
        <Curriculum
          heading="プロのスキルを2ヶ月に凝縮"
          period="第2期 2020年9月中旬より開講予定"
          curriculumItems={[
            {
              periodImgPc: staticPath("/static/images/course/period-6weeks-pc.svg"),
              periodImgSp: staticPath("/static/images/course/period-6weeks-sp.svg"),
              date: "",
              heading: "基礎講座",
              caption: "ライティングの基礎を知る",
              body: "ライティングの基礎を知り、「情報を整理して、正しく伝える」技術を身につけていただきます。講座終了後のゴールは、「長期インターン先で即戦力のライターとして活躍できること」です。",
            },
            {
              periodImgPc: staticPath("/static/images/course/period-2weeks-pc.svg"),
              periodImgSp: staticPath("/static/images/course/period-2weeks-sp.svg"),
              date: "",
              heading: "実践講座",
              caption: "プロのライターとしてデビューする",
              body: "第一線で活躍するプロのライター（編集者）とともに取材を行い、WEBメディアで記事を公開。ポートフォリオを作成し、基礎講座と併せて計2ヶ月間で「フリーランスライター」としてデビューしていただきます。",
            },
          ]}
          requirementItems={[
            {
              requirementURL: "#",
              requirementImg: staticPath("/static/images/course/writing/recruitment/somewrite_thumbnail.jpg"),
              requirementHeading: "プログラミングの魅力を伝える！Webメディアの運営インターンを募集",
              companyName: "サムライト株式会社",
              companyImg: staticPath("/static/images/course/writing/recruitment/somewrite_logo.jpg"),
            },
            {
              requirementURL: "#",
              requirementImg: staticPath("/static/images/course/writing/recruitment/protostar_thumbnail.png"),
              requirementHeading:
                "国内最大級の起業家支援メディアでライティング・起業家へのインタビュー取材に挑戦してみませんか？",
              companyName: "プロトスター株式会社",
              companyImg: staticPath("/static/images/course/writing/recruitment/protostar_logo.png"),
            },
          ]}
          recommendItems={[
            "文章力を向上させたい方",
            "ライター・編集者、広報として活躍したい方",
            "ライターとして有識者や経営者の思考に触れたい方",
            "フリーランスとして、時間と場所にとらわれないワークスタイルに挑戦したい方",
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/writing/mv.png")}
          courseName="ライティング講座"
          courseLineUrl={course.lineUrl}
          disabled
          hideBaloon
        />
        <Price
          items={[
            {
              target: "ベーシックコース（基礎講座）",
              price: 118200,
              splitNum: 6,
            },
            {
              target: "プロフェッショナルコース（基礎 + 実践講座）",
              price: 148000,
              splitNum: 8,
            },
          ]}
        />
        <PromoteArea
          bgPath={staticPath("/static/images/course/writing/mv.png")}
          courseName="ライティング講座"
          courseLineUrl={course.lineUrl}
          disabled
          hideBaloon
        />
        <Consultation job="編集者/ライター" />
        <PromoteArea
          bgPath={staticPath("/static/images/course/writing/mv.png")}
          courseName="ライティング講座"
          courseLineUrl={course.lineUrl}
          disabled
          hideBaloon
        />
        <Observer>
          {() => (
            <>
              {/* <Dialog
                open={uiStore.authPromptModalOpen}
                heading="登録またはログインが必要です"
                mainAction={{
                  label: "登録する",
                  action: () =>
                    window.open(uiStore.redirectUrlAfterAuth, "_self"),
                }}
                subAction={{
                  label: "キャンセル",
                  action: () => uiStore.toggleAuthPromptModal(),
                }}
              >
                エントリー/お気に入りを利用するためには、新規登録またはログインが必要です。
              </Dialog>
              <Dialog
                open={uiStore.confirmCourseApplyModalOpen}
                heading="講座申し込みの確認"
                mainAction={{
                  label: "申し込む",
                  action: async () => {
                    toggleLoading()
                    uiStore.toggleConfirmCourseApplyModal()
                    uiStore.toggleCompleteApplyCourseModal()
                    // 広告CV設定のため、敢えてリロードさせる
                    location.href = `${
                      process.env.APP_HOST + location.pathname
                    }?openModal=applyComplete`
                    toggleLoading()
                  },
                  className: "gtm_course-complete-application",
                  isLoading: isLoading,
                }}
                subAction={{
                  label: "キャンセル",
                  action: () => uiStore.toggleConfirmCourseApplyModal(),
                }}
              >
                こちらの講座は<PaidAlert>有料</PaidAlert>となります。
                <br />
                申し込みを確定してよろしいですか？
              </Dialog> */}
              <CourseInputPersonalDataModal
                open={uiStore.authPromptModalOpen}
                close={() => uiStore.toggleAuthPromptModal()}
                updateTmpUserInfo={updateTmpUserInfo}
                mainAction={() => {
                  uiStore.toggleAuthPromptModal()
                  uiStore.toggleCourseSchduleSelectModal()
                }}
              />
              <CourseSchduleSelectModal
                open={uiStore.courseSchduleSelectModalOpen}
                selectedScheduleValues={selectedScheduleValues}
                onSelectSchedule={handleSelectSchedule}
                mainAction={mainActionForSchduleSelectModal}
                subAction={() => uiStore.toggleCourseSchduleSelectModal()}
                isLoading={isLoading}
              />
              <CourseApplyDoneModal
                open={uiStore.completeApplyCourseModalOpen}
                close={() => uiStore.toggleCompleteApplyCourseModal()}
                addFriend={() => window.open(course.lineUrl)}
                lineQrImageUrl={course.lineQrImageUrl}
              />
            </>
          )}
        </Observer>
        <Footer />
      </>
    )
  }
}

export default withRouter(WritingCourse)
